// src/hooks/useDynamicManifest.ts

import { useEffect } from 'react';

interface UseDynamicManifestOptions {
    name: string | null | undefined;
    logoUrl: string | null | undefined;
    themeColor: string | null | undefined;
}

/**
 * vite-plugin-pwa menghasilkan satu manifest.webmanifest statis saat build (nama & ikon
 * "Djitu" hardcoded di vite.config.ts) — sama untuk SEMUA domain whitelabel, karena file
 * itu di-serve apa adanya dari build output. Ini sebabnya hasil "Add to Home Screen" di
 * domain whitelabel manapun selalu menampilkan branding default, bukan branding whitelabel
 * tersebut, meski favicon/title/theme-color sudah dinamis (lihat useFavicon.ts + AdminLayout).
 *
 * Hook ini membangun manifest baru di client (Blob URL) dari skin data whitelabel yang aktif,
 * lalu menukar href tag <link rel="manifest"> — mengikuti pola persis useFavicon.ts. Kalau
 * data whitelabel belum ada (mis. masih loading), tag manifest statis default tetap dipakai.
 */
const useDynamicManifest = ({ name, logoUrl, themeColor }: UseDynamicManifestOptions): void => {
    useEffect(() => {
        if (!name || !logoUrl) {
            return;
        }

        const manifest = {
            name,
            short_name: name,
            description: `${name} is a web-based application that provides a platform for users to manage their properties`,
            theme_color: themeColor || '#ffffff',
            background_color: '#ffffff',
            display: 'standalone',
            start_url: '/',
            icons: [
                { src: logoUrl, sizes: '192x192', type: 'image/png' },
                { src: logoUrl, sizes: '512x512', type: 'image/png' },
            ],
        };

        const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
        const blobUrl = URL.createObjectURL(blob);

        let link: HTMLLinkElement | null = document.querySelector("link[rel='manifest']");

        if (!link) {
            link = document.createElement('link');
            link.rel = 'manifest';
            document.getElementsByTagName('head')[0].appendChild(link);
        }

        link.href = blobUrl;

        // Lepas Blob URL lama saat data berubah/unmount supaya tidak bocor memori.
        return () => {
            URL.revokeObjectURL(blobUrl);
        };
    }, [name, logoUrl, themeColor]);
};

export default useDynamicManifest;
